import { useCallback, useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { isAfter, parseISO, subDays } from "date-fns";
import {
  Account,
  AccountTypeEnum,
  useListAndSearchAllAccounts,
  useManualSyncRequired,
} from "@rentablo/api/apiv1";
import { useMandatorContext } from "@rentablo/theming";
import { isKpmgReportingEnabledInMetadata } from "@helpers";
import { AccountsGroup, ExtendedAccount } from "./types";

const AUTO_SYNC_MAX_AGE_DAYS = 2;

export const getImportType = (
  account: Account
): "userGenerated" | "manual" | "auto" => {
  const {
    lastAutoSyncDate,
    lastSyncDate,
    bankConnectionType,
    bankConnectionId,
  } = account;

  if (!bankConnectionId) return "userGenerated";

  const isLatest = lastAutoSyncDate === lastSyncDate;
  const isFresh =
    !!lastAutoSyncDate &&
    isAfter(
      parseISO(lastAutoSyncDate),
      subDays(new Date(), AUTO_SYNC_MAX_AGE_DAYS)
    );
  const isFinApi = bankConnectionType === "FIN_API";

  return isLatest && isFresh && isFinApi ? "auto" : "manual";
};

const ALLOWED_GROUPS_PER_MANDATOR = {
  "kpmg-germany": ["01_depot", "02_cash"],
  default: undefined,
};

const getAllowedGroupsForMandator = (mandatorSlug: string) => {
  return (
    ALLOWED_GROUPS_PER_MANDATOR[
      mandatorSlug as keyof typeof ALLOWED_GROUPS_PER_MANDATOR
    ] ?? []
  );
};

export const groupOrder: AccountTypeEnum[] = [
  "01_depot",
  "02_cash",
  "04_insurance",
  "05_material_assets",
];

const extendAccount = (
  account: Account,
  syncRequiredById: Record<number, boolean>
): ExtendedAccount => ({
  ...account,
  syncRequired: syncRequiredById[account.id] ?? false,
  importType: getImportType(account),
  lastChanged: account.timestamps?.updatedAt ?? account.timestamps?.createdAt,
  isKpmgReportingEnabled: isKpmgReportingEnabledInMetadata(account.metadata),
});

export const useAccountsByGroup = ({ showHidden }: { showHidden: boolean }) => {
  const { mandatorSlug, isKpmg } = useMandatorContext();
  const { data, ...rest } = useListAndSearchAllAccounts(
    {
      // @ts-ignore
      order: ["name desc"],
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const accountIds = useMemo(() => accounts.map((a) => a.id) ?? [], [accounts]);

  const { data: syncStatusList } = useManualSyncRequired(
    { accountId: accountIds, cutoff: isKpmg ? 2 : 7 },
    { enabled: accountIds.length > 0 }
  );

  const syncRequiredById = useMemo(
    () =>
      syncStatusList?.syncStatuses?.reduce(
        (acc, s) => {
          if (s.accountId != null) {
            acc[s.accountId] = s.manualSyncRequired ?? false;
          }
          return acc;
        },
        {} as Record<number, boolean>
      ) ?? {},
    [syncStatusList]
  );

  const parentHiddenStates = useMemo(
    () =>
      accounts.reduce(
        (acc, { id, isHidden }) => {
          acc[id] = isHidden;
          return acc;
        },
        {} as { [key: number]: boolean | undefined }
      ) ?? {},
    [accounts]
  );

  const checkIsHidden = useCallback(
    (account: Account) =>
      account.isHidden ||
      // A reference account fans out across every linked depot, so it is only
      // hidden when all of those depots are hidden (not just the first one).
      (!!account.depotIds?.length &&
        account.depotIds.every((depotId) => parentHiddenStates[depotId])),
    [parentHiddenStates]
  );

  const allExtendedAccounts = useMemo(
    () => accounts.map((a) => extendAccount(a, syncRequiredById)),
    [accounts, syncRequiredById]
  );

  const hiddenAccountsCount = useMemo(
    () => allExtendedAccounts.filter((a) => checkIsHidden(a)).length,
    [allExtendedAccounts, checkIsHidden]
  );

  const extendedAccounts = useMemo(
    () =>
      showHidden
        ? allExtendedAccounts
        : allExtendedAccounts.filter((a) => !checkIsHidden(a)),
    [allExtendedAccounts, showHidden, checkIsHidden]
  );

  const refMapByDepotId = useMemo(() => {
    return extendedAccounts.reduce<Record<number, ExtendedAccount[]>>(
      (acc, ext) => {
        (ext.depotIds ?? []).forEach((depotId) => {
          (acc[depotId] ??= []).push(ext);
        });
        return acc;
      },
      {}
    );
  }, [extendedAccounts]);

  const groupedAccounts = useMemo(() => {
    const grouped = extendedAccounts.reduce<AccountsGroup[]>((acc, ext) => {
      if (ext.depotIds?.length) {
        return acc;
      }
      const existing = acc.find((g) => g.title === ext.type);
      if (existing) {
        existing.accounts.push(ext);
      } else {
        acc.push({ title: ext.type, accounts: [ext] });
      }
      return acc;
    }, []);

    const allowedGroups = getAllowedGroupsForMandator(mandatorSlug);
    const filteredGrouped = allowedGroups.length
      ? grouped.filter((group) => allowedGroups.includes(group.title))
      : grouped;

    return filteredGrouped.sort(
      (a, b) => groupOrder.indexOf(a.title) - groupOrder.indexOf(b.title)
    );
  }, [extendedAccounts, mandatorSlug]);

  return {
    ...rest,
    accounts: extendedAccounts,
    groupedAccounts: groupedAccounts || [],
    refMapByDepotId,
    hiddenAccountsCount,
    groups: groupOrder.filter((group) =>
      groupedAccounts.some(({ title }) => title === group)
    ),
  };
};
